import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { UIConfigVariable, UISetupConfig } from 'types/ui-setup-config';
import type { System } from 'types/system';
import type { SystemComponent } from 'types/component';
import type { Quantity, Unit } from 'types/unit-quantity';
import httpService from '../../services/http';
import useSystem from '../../services/system/useSystem';
import { useUserDataContext } from '../UserDataProvider';
import { getVariableList, getInitialValues, transformVariableObjectToArray } from './helper';

type StructureContextType = {
  systemId: number | undefined;
  system: System;
  setSystemId: React.Dispatch<React.SetStateAction<number | undefined>>;
  systemsList: Array<{
    value: number;
    label: string;
  }>;
  refetchSystemsList: () => void;
  isLoadingStructure: boolean;
  interactiveSimulationMode: boolean;
  setInteractiveSimulationMode: React.Dispatch<React.SetStateAction<boolean>>;
  outputInterval: number;
  setOutputInterval: React.Dispatch<React.SetStateAction<number>>;
  quantities: Array<Quantity>;
  units: Array<Unit>;
  imagesList: Array<{
    id: number;
    name: string;
    img: string;
    type: 'svg' | 'png';
    system: number;
  }>;
  systemComponents: Array<SystemComponent>;
  uiSetupConfig: UISetupConfig;
  parameterList: Array<UIConfigVariable>;
  uiVariablesList: Array<UIConfigVariable>;
  parameterMenuIcons: Array<string>;
  initialValues: Record<string, any>;
  systemConfigurationVersion: 'v1' | 'v2';
};

type Props = {
  children: React.ReactNode;
};

export const StructureContext: React.Context<StructureContextType | undefined> = React.createContext<
  StructureContextType | undefined
>(undefined);

const StructureProvider = ({ children }: Props) => {
  const [systemId, setSystemId] = useState<number | undefined>(undefined);
  const [interactiveSimulationMode, setInteractiveSimulationMode] = useState(false);
  const [outputInterval, setOutputInterval] = useState(1);

  const { email } = useUserDataContext();

  const {
    data: systemsList,
    refetch: refetchSystemsList,
    isLoading: isLoadingSystemsList,
  } = useQuery<{ value: number; label: string }[]>({
    queryKey: ['systems', email],
    queryFn: async () => {
      const response = await httpService.get('systems/', {}, 'json', true, 'v2');
      const sortedList = (response.data as { id: number; name: string; is_public: boolean }[]).sort((a, b) => {
        if (a.is_public === b.is_public) return 0;
        return a.is_public ? 1 : -1;
      });

      return sortedList.map((system) => ({
        value: system.id,
        label: system.is_public ? `${system.name} (public)` : system.name,
      }));
    },
    enabled: !!email,
  });

  const { data: system, isLoading: isLoadingSystem } = useSystem(systemId, { enabled: !!email });

  const { data: systemComponents, isLoading: isLoadingSystemComponents } = useQuery({
    queryKey: ['systemComponents', systemId, email],
    queryFn: async ({ queryKey }) => {
      const [, id] = queryKey as ['systemComponents', number | undefined, number | undefined];
      if (!id) throw new Error('No system id');
      const response = await httpService.get(`systems/${id}/components/`, {}, 'json', true, 'v2');
      return response.data;
    },
    enabled: !!email && !!systemId,
  });

  const { data: images, isLoading: isLoadingImages } = useQuery({
    queryKey: ['images', systemId, email],
    queryFn: async ({ queryKey }) => {
      const [, id] = queryKey as ['images', number | undefined, number | undefined];
      if (!id) throw new Error('No system id');
      const response = await httpService.get(
        `image/`,
        {
          headers: null,
          system: id,
        },
        'json',
        true,
      );
      return response.data;
    },
    enabled: !!email && !!systemId,
  });

  const { data: quantities, isLoading: isLoadingQuantities } = useQuery({
    queryKey: ['quantities'],
    queryFn: async () => {
      const response = await httpService.get('units/quantity/', {}, 'json', true);
      return response.data;
    },
    enabled: !!email,
  });

  const { data: units, isLoading: isLoadingUnits } = useQuery({
    queryKey: ['units'],
    queryFn: async () => {
      const response = await httpService.get('units/unit/', {}, 'json', true);
      return response.data;
    },
    enabled: !!email,
  });

  const isLoadingStructure = useMemo(() => {
    return (
      isLoadingSystem ||
      isLoadingSystemComponents ||
      isLoadingImages ||
      isLoadingQuantities ||
      isLoadingUnits ||
      isLoadingSystemsList
    );
  }, [
    isLoadingSystem,
    isLoadingSystemComponents,
    isLoadingImages,
    isLoadingQuantities,
    isLoadingUnits,
    isLoadingSystemsList,
  ]);

  useEffect(() => {
    if (systemId !== undefined) {
      localStorage.setItem('persistentSystemSelection', JSON.stringify(systemId));
    } else if (!isLoadingSystemsList) {
      const persistentSelectedSystemId = JSON.parse(localStorage.getItem('persistentSystemSelection') || 'null');
      if (
        persistentSelectedSystemId &&
        systemsList?.find((systemItem) => systemItem.value === persistentSelectedSystemId)
      ) {
        setSystemId(persistentSelectedSystemId);
      } else if (systemsList?.[0]) {
        setSystemId(systemsList[0].value);
      }
    }
  }, [systemId, systemsList, isLoadingSystemsList]);

  /**
   * Computes derived values from system configuration:
   * - uiSetupConfig: UI setup config without the 'Hidden' menu, used for rendering menus and forms
   * - setupVariables: All configuration variables from UI setup (including hidden ones)
   * - visibleSetupVariables: Only non-hidden configuration variables
   * - menuIcons: Icons for each visible menu item
   * - initialValues: Default values for all parameters
   * - systemConfigurationVersion: API version (v1 or v2)
   */
  const memoizedContextValues = useMemo<{
    uiSetupConfig: UISetupConfig;
    setupVariables: UIConfigVariable[];
    visibleSetupVariables: UIConfigVariable[];
    menuIcons: string[];
    initialValues: Record<string, any>;
    systemConfigurationVersion: 'v1' | 'v2';
  } | null>(() => {
    if (!system) return null;
    const rawSetupConfig = system.ui_setup_config ?? [];
    const uiSetupConfig = rawSetupConfig.filter((el: { name: string }) => el.name !== 'Hidden');

    const setupVariables = getVariableList(rawSetupConfig, systemComponents);
    return {
      uiSetupConfig,
      setupVariables,
      visibleSetupVariables: getVariableList(uiSetupConfig, systemComponents),
      menuIcons: uiSetupConfig.map((el: { icon?: string }) => el.icon || ''),
      initialValues: getInitialValues(setupVariables, systemComponents),
      systemConfigurationVersion: system.result_charts?.length > 0 ? 'v2' : 'v1',
    };
  }, [system, systemComponents]);

  const normalizedVariables = useMemo(() => {
    if (!system || !system?.variables) return { parameters: [], variables: [], outputs: [], inputs: [] };
    const { parameters, variables, outputs, inputs } = system.variables;
    return {
      parameters: transformVariableObjectToArray(parameters),
      variables: transformVariableObjectToArray(variables),
      outputs: transformVariableObjectToArray(outputs),
      inputs: transformVariableObjectToArray(inputs),
    };
  }, [system]);

  return (
    <StructureContext.Provider
      value={{
        systemId,
        system: {
          ...system,
          variables: normalizedVariables,
        },
        setSystemId,
        systemsList: systemsList || [],
        refetchSystemsList,
        isLoadingStructure,
        interactiveSimulationMode,
        setInteractiveSimulationMode,
        outputInterval,
        setOutputInterval,
        units: units || [],
        quantities: quantities || [],
        imagesList: images || [],
        systemComponents: systemComponents || [],
        uiSetupConfig: memoizedContextValues?.uiSetupConfig || [],
        initialValues: memoizedContextValues?.initialValues || {},
        parameterList: memoizedContextValues?.setupVariables || [],
        uiVariablesList: memoizedContextValues?.visibleSetupVariables || [],
        parameterMenuIcons: memoizedContextValues?.menuIcons || [],
        systemConfigurationVersion: memoizedContextValues?.systemConfigurationVersion ?? 'v1',
      }}
    >
      {children}
    </StructureContext.Provider>
  );
};

export const useStructureContext = (): StructureContextType => {
  const context = useContext(StructureContext);

  if (context === undefined) {
    throw new Error('useStructureContext must be used within a StructureProvider');
  }

  return context;
};
export default StructureProvider;
